export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes) {
    return '';
  }
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) {
    return `${gb.toFixed(1)}GB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(0)}MB`;
};

export const decodeHtml = (html: string): string => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

export function parseDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) {
    return null;
  }
  // yt-dlp writes upload_date as YYYYMMDD
  if (/^\d{8}$/.test(dateStr)) {
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
    const day = parseInt(dateStr.substring(6, 8), 10);
    return new Date(year, month, day);
  }
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(dateStr: string | null | undefined): string | null {
  const date = parseDate(dateStr);
  return date ? date.toLocaleDateString() : null;
}

// Convert yt-dlp's YYYYMMDD upload_date into a UTC-midnight ISO string, matching
// how the backend stores channelvideos.publishedAt (so an override set on the
// client renders identically to a value fetched from the server). Strings that
// are already ISO pass through. Returns null if unparseable.
export function uploadDateToIso(dateStr: string | null | undefined): string | null {
  if (!dateStr) {
    return null;
  }
  if (/^\d{8}$/.test(dateStr)) {
    const iso = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}T00:00:00.000Z`;
    return isNaN(new Date(iso).getTime()) ? null : iso;
  }
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
};

export function formatDateTime(dateStr: string | null | undefined): string | null {
  const date = parseDate(dateStr);
  if (!date) {
    return null;
  }
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString(undefined, DATE_TIME_OPTIONS)}`;
}

// formatDateTime rendered in an explicit IANA zone (the server's), so times
// labeled "server time" read correctly from any browser. Falls back to the
// browser zone when the name is missing or unknown.
export function formatDateTimeInZone(
  dateStr: string | null | undefined,
  timeZone: string | null | undefined
): string | null {
  const date = parseDate(dateStr);
  if (!date) {
    return null;
  }
  if (!timeZone) {
    return formatDateTime(dateStr);
  }
  try {
    return `${date.toLocaleDateString(undefined, { timeZone })} ${date.toLocaleTimeString(undefined, { ...DATE_TIME_OPTIONS, timeZone })}`;
  } catch {
    return formatDateTime(dateStr);
  }
}

const ADDED_DATE_SHORT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: '2-digit',
};

const ADDED_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

export function formatAddedDateParts(
  iso: string | null | undefined
): { date: string; time: string } | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], ADDED_TIME_OPTIONS),
  };
}

export function formatAddedDateTime(iso: string | null | undefined): string {
  const parts = formatAddedDateParts(iso);
  return parts ? `${parts.date} ${parts.time}` : '';
}

export function formatAddedDate(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const day = date.toLocaleDateString(undefined, ADDED_DATE_SHORT_OPTIONS);
  const time = date.toLocaleTimeString([], ADDED_TIME_OPTIONS);
  return `${day} ${time}`;
}
